import { useEffect, useState } from 'react';
import { Box, Drawer, IconButton, Stack, Tab, Tabs, Typography, alpha, useTheme } from '@mui/material';
import { X } from 'lucide-react';
import type { HansAckerFinding } from './types';
import { InlineCode, Markdown } from './Rich';
import { SeverityBadge } from './FindingsTable';
import { dedentBlock, severityColor, splitLocations } from './utils';

const TABS = ['Overview', 'Evidence', 'Analysis', 'Reproduce', 'Fix', 'Confidence'] as const;

const TAB_SECTIONS: Record<number, [string, string?][]> = {
  1: [['Evidence']],
  2: [['Technical Analysis'], ['Contextual CVSS']],
  3: [['Proof of Concept']],
  4: [['Code Analysis', 'Code analysis'], ['Remediation'], ['Fix Verification', 'Fix verification']],
  5: [['Confidence Rationale', 'Confidence rationale'], ['Counterevidence'], ['What Would Change This Severity', 'What would change this severity'], ['Assumptions']],
};

const STAT_KEYS = ['CVSS', 'Advisory CVSS', 'Confidence', 'Fix Effort', 'CWE'];
const PACKAGE_KEYS = ['Package', 'Ecosystem', 'Installed Version', 'Fixed Version'];
const MONO_KEYS = ['Target', 'Endpoint', 'Method', 'CVE', 'Contextual CVSS Vector', 'Surface'];
const mono = { fontFamily: "'Consolas', monospace" };

function SectionHeading({ children, mt = 0 }: { children: string; mt?: number }) {
  return <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color: 'text.disabled', mt, mb: 1 }}>{children}</Typography>;
}

function Callout({ tone, children }: { tone: 'warn' | 'info'; children: React.ReactNode }) {
  const color = tone === 'warn' ? '#f59e0b' : '#3b82f6';
  return <Box sx={{ mb: 2, px: 1.75, py: 1.25, borderRadius: 1.5, fontSize: 12.5, border: '1px solid', borderColor: alpha(color, 0.35), bgcolor: alpha(color, 0.08), color: 'text.primary' }}>{children}</Box>;
}

function Card({ children, accent }: { children: React.ReactNode; accent?: string }) {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 1.5, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.text.primary, 0.025), ...(accent ? { borderLeft: `3px solid ${accent}` } : {}) }}>
      {children}
    </Box>
  );
}

function Overview({ finding }: { finding: HansAckerFinding }) {
  const theme = useTheme();
  const color = severityColor(finding.severity);
  const meta = Object.fromEntries(finding.doc.meta);
  const tile = (key: string, main = false) => meta[key] ? (
    <Box
      key={key}
      sx={{
        p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.text.primary, 0.03),
        ...(main ? { borderTop: `3px solid ${color}`, background: `linear-gradient(180deg, ${alpha(color, 0.12)}, transparent 70%)` } : {}),
      }}
    >
      <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.disabled' }}>{key === 'Fix Effort' ? 'Fix effort' : key}</Typography>
      <Typography sx={{ mt: 0.5, fontSize: main ? 26 : 18, fontWeight: 800, lineHeight: 1.2, color: main ? color : 'text.primary', overflowWrap: 'anywhere' }}>{meta[key]}</Typography>
    </Box>
  ) : null;
  const rows = finding.doc.meta.filter(([k]) => !STAT_KEYS.includes(k) && !PACKAGE_KEYS.includes(k));
  const s = finding.doc.sections;

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 1.25, mb: 2 }}>
        {tile('CVSS', true)}{tile('Advisory CVSS', !meta['CVSS'])}{tile('Confidence')}{tile('Fix Effort')}{tile('CWE')}
      </Box>

      {meta['Installed Version'] && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', mb: 2, p: 1.75, borderRadius: 2, border: '1px solid', borderColor: alpha('#22c55e', 0.3), bgcolor: alpha('#22c55e', 0.07) }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {meta['Package']}
            <Typography component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>{meta['Ecosystem']}</Typography>
          </Typography>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ ml: 'auto', ...mono, fontSize: 14, fontWeight: 700 }}>
            <Box component="span" sx={{ color: '#ef4444', bgcolor: alpha('#ef4444', 0.12), borderRadius: 1, px: 1.1, py: 0.3 }}>{meta['Installed Version']}</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>{'\u2192'}</Box>
            <Box component="span" sx={{ color: '#16a34a', bgcolor: alpha('#22c55e', 0.14), borderRadius: 1, px: 1.1, py: 0.3 }}>{meta['Fixed Version']}</Box>
          </Stack>
        </Stack>
      )}

      {s['Description'] && <Card><SectionHeading>Description</SectionHeading><Markdown>{s['Description']}</Markdown></Card>}
      {s['Impact'] && <Card accent={color}><SectionHeading>Impact</SectionHeading><Markdown>{s['Impact']}</Markdown></Card>}

      <SectionHeading mt={3}>Details</SectionHeading>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {rows.map(([k, v], i) => (
          <Box key={k} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: { xs: 0.25, sm: 2 }, px: 2, py: 1.1, fontSize: 13, bgcolor: i % 2 ? alpha(theme.palette.text.primary, 0.025) : 'transparent', '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' } }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.disabled', pt: 0.25 }}>{k}</Typography>
            <Box sx={{ overflowWrap: 'anywhere', ...(MONO_KEYS.includes(k) ? { ...mono, fontSize: 12 } : {}) }}>{v}</Box>
          </Box>
        ))}
      </Box>
    </>
  );
}

function SectionTab({ finding, tab }: { finding: HansAckerFinding; tab: number }) {
  const s = finding.doc.sections;
  const present = TAB_SECTIONS[tab].filter(([name]) => s[name]);

  if (tab === 3 && !s['Proof of Concept']) {
    return (
      <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>
        {finding.type === 'dep'
          ? 'No reproduction steps \u2014 this is a dependency advisory identified from the lockfile or manifest.'
          : 'No reproduction steps were recorded for this item.'}
      </Typography>
    );
  }
  if (!present.length) return <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>The report has no content for this section.</Typography>;

  return (
    <>
      {tab === 1 && finding.redacted && <Callout tone="info">Secret values are redacted in this view. Rotate the original credentials.</Callout>}
      {present.map(([name, label], i) => (
        <Box key={name} sx={{ mt: i ? 3 : 0 }}>
          <SectionHeading>{label || name}</SectionHeading>
          {name === 'Code Analysis'
            ? splitLocations(s[name]).map((block, i) => <Card key={i}><Markdown>{dedentBlock(block)}</Markdown></Card>)
            : <Markdown>{s[name]}</Markdown>}
        </Box>
      ))}
      {tab === 3 && <Callout tone="warn">Static finding. These steps were derived from the source and were not executed against a running instance.</Callout>}
    </>
  );
}

export default function FindingDrawer({ finding, onClose }: { finding: HansAckerFinding | null; onClose: () => void }) {
  const [tab, setTab] = useState(0);
  useEffect(() => { setTab(0); }, [finding?.id]);

  return (
    <Drawer anchor="right" open={!!finding} onClose={onClose} PaperProps={{ sx: { width: 'min(760px, 96vw)', backgroundImage: 'none' } }}>
      {finding && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <SeverityBadge severity={finding.severity} />
              <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', bgcolor: alpha('#60a5fa', 0.16), borderRadius: 0.75, px: 1, py: 0.3 }}>{finding.folder}</Box>
              <InlineCode>{finding.id}</InlineCode>
              <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ ml: 'auto' }}><X size={18} /></IconButton>
            </Stack>
            <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.35 }}>{finding.doc.title}</Typography>
          </Box>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 44 } }}>
            {TABS.map((t) => <Tab key={t} label={t} />)}
          </Tabs>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5, pb: 6 }}>
            {tab === 0 ? <Overview finding={finding} /> : <SectionTab finding={finding} tab={tab} />}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
